import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, PawPrint, BookOpen, UserRound, LogOut, Building2, ScrollText, Layers } from "lucide-react";
import DemoChrome from "./DemoChrome";
import i18n from "../i18n";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useOnline } from "../lib/draft";
import { cx } from "./kit";

export function Wordmark({ to = "/" }) {
  return (
    <Link to={to} data-testid="wordmark" className="flex min-h-[44px] items-center gap-2 shrink-0" aria-label="Valeriia">
      <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-[12px] bg-[hsl(var(--peach))] border border-[hsl(12_60%_86%)]">
        <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-[hsl(140_24%_78%)]" />
        <span className="font-display text-base font-extrabold text-[hsl(14_56%_36%)]">V</span>
      </span>
      <span className="font-display text-xl font-extrabold tracking-tight text-[hsl(24_14%_18%)]">Valeriia</span>
    </Link>
  );
}

export function LangSwitch() {
  const { me, setMe } = useAuth() || {};
  const cur = i18n.language?.startsWith("ru") ? "ru" : "en";
  const change = async (lng) => {
    if (lng === cur) return;
    localStorage.setItem("valeriia_lang", lng);
    localStorage.setItem("valeriia_lang_manual", "1");
    await i18n.changeLanguage(lng);
    if (me?.user) {
      try {
        const { data } = await api.put("/auth/profile", { interface_language: lng });
        setMe(data);
      } catch {}
    }
  };
  return (
    <div role="group" aria-label="Language / Язык" data-testid="language-switcher" className="inline-flex items-center rounded-full border bg-white p-0.5 text-sm font-semibold">
      {[["ru", "Русский"], ["en", "English"]].map(([l, label], i) => (
        <span key={l} className="flex items-center">
          {i === 1 && <span aria-hidden className="px-0.5 text-muted-foreground">/</span>}
          <button data-testid={`language-switcher-${l}`} lang={l} aria-pressed={cur === l} onClick={() => change(l)}
            className={cx("min-h-[44px] rounded-full px-2.5 transition-colors", cur === l ? "bg-[hsl(var(--lavender))] text-[hsl(268_32%_28%)]" : "text-muted-foreground hover:text-foreground")}>
            {label}
          </button>
        </span>
      ))}
    </div>
  );
}

function ContextPill() {
  const { t } = useTranslation();
  const { me, setMe } = useAuth();
  const navigate = useNavigate();
  const u = me.user;
  const mems = me.memberships || [];
  const options = [...mems.map((m) => ({ v: `g:${m.workspace_id}`, label: `${m.workspace?.name} · ${t(`role.${m.role}`)}` }))];
  if (me.owner_dog_count > 0 || u.role_choice === "owner") options.push({ v: "owner", label: `${t("role.owner_space")} · ${t("role.owner")}` });
  const value = u.active_role === "owner" ? "owner" : `g:${u.active_workspace_id}`;
  const onChange = async (e) => {
    const v = e.target.value;
    const body = v === "owner" ? { role: "owner" } : { role: "groomer", workspace_id: v.slice(2) };
    const { data } = await api.post("/auth/active", body);
    setMe(data);
    navigate(v === "owner" ? "/owner" : "/home");
  };
  const active = options.find((o) => o.v === value);
  return (
    <label className="flex min-w-0 items-center gap-1.5 rounded-full border bg-[hsl(var(--sage))] px-3 py-1.5 text-sm font-semibold text-[hsl(140_35%_20%)]" data-testid="workspace-role-pill">
      <Building2 className="h-4 w-4 shrink-0" aria-hidden />
      <span className="sr-only">{t("nav.active_context")}</span>
      {options.length > 1 ? (
        <select data-testid="workspace-role-select" value={value} onChange={onChange} className="min-w-0 max-w-[200px] sm:max-w-[280px] truncate bg-transparent font-semibold focus:outline-none">
          {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
      ) : <span className="truncate max-w-[200px] sm:max-w-[280px]">{active?.label || t("nav.no_workspace")}</span>}
    </label>
  );
}

export function OfflineBar() {
  const { t } = useTranslation();
  const online = useOnline();
  if (online) return null;
  return <div data-testid="offline-bar" role="status" className="bg-[hsl(var(--peach))] px-4 py-2 text-center text-sm font-semibold text-[hsl(14_56%_26%)]">{t("offline.bar")}</div>;
}

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const owner = me?.user?.active_role === "owner";
  const sandbox = !!me?.user?.sandbox_id;
  const nav = owner
    ? [["/owner", Home, "nav.owner_home"], ["/profile", UserRound, "nav.profile"]]
    : [["/home", Home, "nav.home"], ["/practice", PawPrint, "nav.practice"], ["/library", BookOpen, "nav.library"], ["/workspace", Building2, "nav.workspace"], ["/audit", ScrollText, "nav.audit"], ["/profile", UserRound, "nav.profile"]];
  if (sandbox) nav.push(["/demo/modules", Layers, "sandbox.nav_modules"]);
  return (
    <div className="min-h-screen">
      <OfflineBar />
      {sandbox && <DemoChrome />}
      <header className="sticky top-0 z-40 border-b bg-[hsl(38_33%_97%/0.9)] backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-2.5">
          <Wordmark to={owner ? "/owner" : "/home"} />
          <div className="order-3 w-full sm:order-none sm:w-auto sm:ml-3 flex min-w-0"><ContextPill /></div>
          <div className="ml-auto flex items-center gap-2">
            <LangSwitch />
            <button data-testid="sign-out-btn" onClick={() => { signOut(); navigate("/login"); }} className="inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-muted" aria-label={t("nav.sign_out")} title={t("nav.sign_out")}>
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
        <nav className="mx-auto max-w-6xl overflow-x-auto px-2" aria-label={t("nav.main")}>
          <ul className="flex gap-1 pb-1.5">
            {nav.map(([to, Icon, key]) => (
              <li key={to}>
                <NavLink to={to} data-testid={`nav-item-${key.split(".")[1]}`} className={({ isActive }) => cx("flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-xl px-3 text-sm font-semibold transition-colors", isActive ? "bg-white border text-foreground" : "text-muted-foreground hover:bg-white/70 hover:text-foreground")}>
                  <Icon className="h-4 w-4" aria-hidden /> {t(key)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-10">{children}</main>
    </div>
  );
}

export function BareLayout({ children }) {
  return (
    <div className="min-h-screen grain">
      <OfflineBar />
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-4">
        <Wordmark to="/" />
        <LangSwitch />
      </header>
      <main className="mx-auto max-w-6xl px-4 pb-12">{children}</main>
    </div>
  );
}
